import React, { memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { getItineraryUniqueKey } from 'lib/cruises/cruiseUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PaneBody from 'components/Common/Pane/PaneBody'
import Expander from 'components/utils/Expander'
import Clickable from 'components/Common/Clickable'
import Heading, { Heading5Style } from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import clsx from 'clsx'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { CruiseType } from 'constants/cruise'

const AccordionSection = styled.div`
  margin-top: ${rem(16)};

  &:nth-child(2) {
    margin-top:${rem(28)};
  }

  &:last-child {
    > * {
      border-bottom-color: transparent;
    }
  }
`
const AccordionHeader = styled(Clickable)`
  display: flex;
  width: 100%;
  padding: ${rem(20)} 0;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.seven};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.S};
  border-bottom:unset;
  padding: ${rem(12)};

  &.open {
    border-bottom-color: transparent;
  }

  &.disable-click {
    pointer-events: none;
    pointer: default;
  }

  &.disable-expand {
    cursor: default;
  }

  &.selected .day-label {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const ArrowContainer = styled.div`
  display: flex;
  justify-content: end;

  transform: rotate(90deg);
  transition: transform 0.2s ease-out;

  &.open {
    transform: rotate(-90deg);
  }
`

const DescriptionWrapper = styled.div`
  padding: ${rem(16)} 0 0 0;
`

const PaneBodyStyled = styled(PaneBody)`
  padding: 0 ${rem(24)} 0 ${rem(18)};
`

const Section = styled.div`
  margin: ${rem(16)} 0 ${rem(16)} 0;
`

const DayLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(4)} ${rem(8)};
`

const BodyTextHeader = styled.div`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-column: 2;
    grid-row: 2;
  }
`

const HeadingMarkdownRender = styled(MarkdownRender)`
  ${Heading5Style}
  color: ${props => props.theme.palette.neutral.default.one};
`

interface Props {
  itineraryItem: App.CruiseItineraryItem
  open: boolean
  isSelected: boolean
  cruiseType: App.CruiseType
  toggleAccordion: (key: string, item: App.CruiseItineraryItem) => void
}

function CruiseAccordion(props: Props) {
  const { itineraryItem, open, toggleAccordion, isSelected, cruiseType } = props
  const { title, port, description, startTime, endTime } = itineraryItem
  const intl = useIntl()

  const isAtSea = !title && !port
  const emptyContent = isAtSea || !description.trim() || !port

  const headingTitle = useMemo(() => {
    const atSeaLabel = intl.formatMessage({
      id: 'aVQT3ByVbs',
      defaultMessage: 'At Sea',
      description: 'Label for at-sea days on cruise itinerary in the cruise section',
    })
    return isAtSea ? atSeaLabel : port?.name || title
  }, [title, isAtSea, port?.name, intl])

  const departureDescription = useMemo(() => (
    [startTime, endTime].filter((t) => !!t && t !== '--').join(' - ')
  ), [startTime, endTime])

  const handleToggleAccordion = useCallback(() => {
    toggleAccordion(getItineraryUniqueKey(itineraryItem), itineraryItem)
  }, [toggleAccordion, itineraryItem])

  return (
    <AccordionSection>
      <AccordionHeader
        className={clsx({ open, selected: isSelected, 'disable-expand': emptyContent }, getThemeClassName({ mobile: isSelected ? 'inverse' : 'default' }))}
        onClick={handleToggleAccordion}
      >
        <Group direction="horizontal" verticalAlign="center">
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <DayLabel className="day-label">
              <Group direction="vertical">
                <Caption variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: 'iY/svW8zYV',
                    defaultMessage: 'Day',
                    description: 'Label for day number on cruise itinerary accordion',
                  })}
                </Caption>
                <BodyText variant="medium" colour="neutral-one" weight="bold">{itineraryItem.startDay}</BodyText>
              </Group>
            </DayLabel>
            {cruiseType === CruiseType.OCEAN && <div>
              <HeadingMarkdownRender content={headingTitle ?? ''} fontSize="medium" type="compact" />

              <BodyTextHeader>
                <BodyText variant="small" colour="neutral-one">
                  {departureDescription}
                </BodyText>
              </BodyTextHeader>
            </div>}
            {cruiseType === CruiseType.RIVER && <div>
              <Heading variant="heading5">{port?.name || ''}</Heading>
              <BodyTextHeader><BodyText variant="medium" colour="neutral-one">{headingTitle}</BodyText></BodyTextHeader>
            </div>}
          </Group>
        </Group>

        {!emptyContent && <ArrowContainer className={clsx({ open })}>
          <LineAngleRightIcon colour="neutral-one" />
        </ArrowContainer>}
      </AccordionHeader>
      {!isAtSea && <Expander expanded={open}>
        <DescriptionWrapper>
          <PaneBodyStyled size="lg-x">
            <Heading variant="heading5">{title}</Heading>
            <Section>
              <MarkdownRender content={description} fontSize="medium" />
            </Section>
          </PaneBodyStyled>
        </DescriptionWrapper>
      </Expander>}
    </AccordionSection>
  )
}

export default memo(CruiseAccordion)
